import { createContext, useEffect, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { publicGet } from '../api/publicClient'
import { APP_NAME } from '../config'
import { injectBrandingStyle } from './injectBrandingStyle'
import type { BrandingConfig } from './tokens'

export interface BrandingContextValue {
  appName: string
  logo: string | null
  isLoading: boolean
}

// Default deckt den Pre-Fetch-/Fehler-Fall ab: neutraler appName aus config.ts,
// kein Logo. So rendert die App auch ohne erfolgreiche Branding-Query normal
// (CSS-Defaults greifen, da dann keine Override-Injektion stattfindet).
export const BrandingContext = createContext<BrandingContextValue>({
  appName: APP_NAME,
  logo: null,
  isLoading: false,
})

export function BrandingProvider({ children }: { children: ReactNode }) {
  // Anonyme Query — Branding muss pre-auth (Login/Status) greifen.
  const { data, isLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: () => publicGet<BrandingConfig>('/api/config/branding'),
    staleTime: Infinity,
    retry: false,
  })

  useEffect(() => {
    if (data?.colors) injectBrandingStyle(data.colors)
  }, [data?.colors])

  const value: BrandingContextValue = {
    // Fallback auf neutralen config-Namen, wenn das Backend null liefert.
    appName: data?.appName ?? APP_NAME,
    logo: data?.logo ?? null,
    isLoading,
  }

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}
